import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CalendarClock,
  ExternalLink,
  FileText,
  HandCoins,
  Info,
  Loader2,
  Upload,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { formatCurrency, formatDate, formatPercent } from '@/lib/formatters';
import { useAuth } from '@/app/AuthProvider';
import { canWrite } from '@/lib/permissions';
import {
  useLoanReceiptSignedUrl,
  useRemoveLoanReceipt,
  useUploadLoanReceipt,
  type LoanReceiptRef,
} from '@/features/loans/hooks';
import {
  buildSchedule,
  isoToLocalDate,
  todayISO,
  type LoanConfig,
  type ScheduledPayment,
} from './loan-math';
import {
  readRecordsFromStorage,
  writeRecordsToStorage,
  type PaymentRecord,
  type PaymentRecords,
} from './loan-records';

interface LoanLedgerProps {
  /** Loan terms. Pass `null` to render the "not configured" state. */
  config: LoanConfig | null;
  /**
   * Stable slug (e.g. "family", "sba"). Used as the localStorage key
   * for payment-record metadata AND as the second path segment for
   * receipt uploads in the `loan-receipts` Storage bucket. Changing
   * the slug orphans both — keep it stable per loan.
   */
  slug: string;
}

/** Accepted receipt file types for the per-row upload input. */
const RECEIPT_ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,.heic,.heif';

export function LoanLedger({ config, slug }: LoanLedgerProps) {
  const { role } = useAuth();
  // Read-only mode is the default for any role without write access to
  // the `loans` module. Today that's `external_lender` — they can see
  // overview stats, the schedule, and view existing receipts, but the
  // date input + Mark Today + Upload + Clear controls all collapse to
  // plain text. The same restriction is enforced server-side: storage
  // RLS in migration 60 grants SELECT but not INSERT / UPDATE / DELETE
  // for external_lender, so even if the UI guard were bypassed the
  // bucket itself would reject the write.
  const readOnly = !canWrite(role, 'loans');

  const [records, setRecords] = useState<PaymentRecords>(() =>
    readRecordsFromStorage(slug),
  );

  useEffect(() => {
    // Don't persist writes from a read-only viewer — the localStorage
    // state should reflect the operator's actions only. (The viewer
    // can't change state anyway because we suppress the controls;
    // this is just belt-and-suspenders against a stray setter.)
    if (readOnly) return;
    writeRecordsToStorage(slug, records);
  }, [records, slug, readOnly]);

  const schedule = useMemo(
    () => (config ? buildSchedule(config) : []),
    [config],
  );

  if (!config) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Loan"
          actions={<Badge variant="muted">Not configured</Badge>}
        />
        <EmptyState
          icon={HandCoins}
          title="No loan terms set"
        />
      </div>
    );
  }

  const totals = computeTotals(schedule, records);
  const nextPayment = schedule.find((p) => !records[p.number]?.paidOn);
  const lastPaid = [...schedule]
    .reverse()
    .find((p) => !!records[p.number]?.paidOn);
  const lastPaymentDueDate = schedule[schedule.length - 1]?.dueDate ?? null;
  const monthlyPayment = schedule[0]?.paymentCents ?? 0;

  /**
   * Merge `patch` into the record for `paymentNumber`. Passing
   * `paidOn: ''` or `receipt: undefined` clears the field; if both
   * are cleared, the payment's entry is removed from the map
   * entirely so the persisted JSON stays compact.
   */
  const updateRecord = (paymentNumber: number, patch: Partial<PaymentRecord>) => {
    setRecords((prev) => {
      const existing = prev[paymentNumber] ?? {};
      const merged: PaymentRecord = { ...existing, ...patch };
      if (patch.paidOn === '') delete merged.paidOn;
      if (Object.prototype.hasOwnProperty.call(patch, 'receipt') && !patch.receipt) {
        delete merged.receipt;
      }
      const next = { ...prev };
      if (!merged.paidOn && !merged.receipt) {
        delete next[paymentNumber];
      } else {
        next[paymentNumber] = merged;
      }
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={config.label}
        actions={
          <Badge variant={totals.allPaid ? 'success' : 'info'}>
            {totals.allPaid
              ? 'Paid in full'
              : `${totals.paidCount} of ${schedule.length} paid`}
          </Badge>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HandCoins className="h-5 w-5 text-old-growth" strokeWidth={1.6} />
            Loan overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-3 lg:grid-cols-4">
            <Stat
              label="Original principal"
              value={formatCurrency(config.principalCents)}
            />
            <Stat
              label="Interest rate"
              value={formatPercent(config.annualRate)}
              hint="Annual / APR"
            />
            <Stat label="Term" value={termLabel(config.termMonths)} />
            <Stat
              label="Monthly payment"
              value={formatCurrency(monthlyPayment)}
            />
            <Stat
              label="Effective date"
              value={formatDate(isoToLocalDate(config.effectiveDate))}
            />
            <Stat
              label="End date"
              value={
                lastPaymentDueDate
                  ? formatDate(isoToLocalDate(lastPaymentDueDate))
                  : '—'
              }
            />
            <Stat
              label="Next payment due"
              value={
                nextPayment
                  ? formatDate(isoToLocalDate(nextPayment.dueDate))
                  : '—'
              }
              hint={nextPayment ? `Payment #${nextPayment.number}` : 'Paid in full'}
            />
            <Stat
              label="Next payment amount"
              value={
                nextPayment ? formatCurrency(nextPayment.paymentCents) : '—'
              }
            />
            <Stat
              label="Last payment paid"
              value={
                lastPaid?.number != null && records[lastPaid.number]?.paidOn
                  ? formatDate(isoToLocalDate(records[lastPaid.number]!.paidOn!))
                  : '—'
              }
              hint={
                lastPaid
                  ? `Payment #${lastPaid.number} · ${formatCurrency(lastPaid.paymentCents)}`
                  : 'No payments recorded yet'
              }
            />
            <Stat
              label="Total amount owed"
              value={formatCurrency(totals.remainingOwedCents)}
            />
            <Stat
              label="Principal remaining"
              value={formatCurrency(totals.principalRemainingCents)}
            />
            <Stat
              label="Total interest (life of loan)"
              value={formatCurrency(totals.totalInterestCents)}
            />
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-old-growth" strokeWidth={1.6} />
              Amortization schedule
            </CardTitle>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Info className="h-3.5 w-3.5" />
            <span>
              {readOnly
                ? 'View-only access · Receipts open in a new tab'
                : 'Paid dates: this browser · Receipts: secured in Atlas storage'}
            </span>
          </div>
        </CardHeader>
        <CardContent className="px-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12 pl-6 text-right">#</TableHead>
                <TableHead>Due date</TableHead>
                <TableHead className="text-right">Payment</TableHead>
                <TableHead className="text-right">Principal</TableHead>
                <TableHead className="text-right">Interest</TableHead>
                <TableHead className="text-right">Remaining balance</TableHead>
                <TableHead>Paid on</TableHead>
                <TableHead className="pr-6">Receipt</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {schedule.map((p) => {
                const record = records[p.number];
                return (
                  <PaymentRow
                    key={p.number}
                    slug={slug}
                    payment={p}
                    paidDate={record?.paidOn ?? null}
                    receipt={record?.receipt ?? null}
                    readOnly={readOnly}
                    onChangePaid={(d) =>
                      updateRecord(p.number, { paidOn: d || '' })
                    }
                    onReceiptUploaded={(ref) =>
                      updateRecord(p.number, { receipt: ref })
                    }
                    onReceiptCleared={() =>
                      updateRecord(p.number, { receipt: undefined })
                    }
                  />
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function PaymentRow({
  slug,
  payment,
  paidDate,
  receipt,
  readOnly,
  onChangePaid,
  onReceiptUploaded,
  onReceiptCleared,
}: {
  slug: string;
  payment: ScheduledPayment;
  paidDate: string | null;
  receipt: LoanReceiptRef | null;
  readOnly: boolean;
  onChangePaid: (dateISO: string) => void;
  onReceiptUploaded: (ref: LoanReceiptRef) => void;
  onReceiptCleared: () => void;
}) {
  const isPaid = !!paidDate;
  return (
    <TableRow
      data-state={isPaid ? 'paid' : 'pending'}
      className={cn(
        'transition-colors',
        isPaid && 'bg-secondary/40 text-muted-foreground',
      )}
    >
      <TableCell className="pl-6 text-right tabular-nums text-muted-foreground">
        {payment.number}
      </TableCell>
      <TableCell className={cn('tabular-nums', isPaid && 'line-through')}>
        {formatDate(isoToLocalDate(payment.dueDate))}
      </TableCell>
      <TableCell className={cn('text-right tabular-nums', isPaid && 'line-through')}>
        {formatCurrency(payment.paymentCents)}
      </TableCell>
      <TableCell className={cn('text-right tabular-nums', isPaid && 'line-through')}>
        {formatCurrency(payment.principalCents)}
      </TableCell>
      <TableCell className={cn('text-right tabular-nums', isPaid && 'line-through')}>
        {formatCurrency(payment.interestCents)}
      </TableCell>
      <TableCell className={cn('text-right tabular-nums', isPaid && 'line-through')}>
        {formatCurrency(payment.remainingBalanceCents)}
      </TableCell>
      <TableCell>
        <PaidOnCell paidDate={paidDate} readOnly={readOnly} onChange={onChangePaid} />
      </TableCell>
      <TableCell className="pr-6">
        <ReceiptCell
          slug={slug}
          paymentNumber={payment.number}
          receipt={receipt}
          readOnly={readOnly}
          onUploaded={onReceiptUploaded}
          onCleared={onReceiptCleared}
        />
      </TableCell>
    </TableRow>
  );
}

function PaidOnCell({
  paidDate,
  readOnly,
  onChange,
}: {
  paidDate: string | null;
  readOnly: boolean;
  onChange: (dateISO: string) => void;
}) {
  // Read-only viewers (e.g. external lender) get a plain text rendering
  // of the paid date so the column doesn't expose an interactive
  // control they can't actually use.
  if (readOnly) {
    return (
      <span className="text-xs tabular-nums text-muted-foreground">
        {paidDate ? formatDate(isoToLocalDate(paidDate)) : '—'}
      </span>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <input
        type="date"
        value={paidDate ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          'h-8 rounded-md border border-input bg-card px-2 text-xs tabular-nums text-foreground transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          !paidDate && 'border-dashed text-muted-foreground',
        )}
      />
      {!paidDate ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={() => onChange(todayISO())}
        >
          Today
        </Button>
      ) : (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => onChange('')}
        >
          Clear
        </Button>
      )}
    </div>
  );
}

/**
 * Receipt upload + view + remove control. Each row owns its own
 * file input (and its own upload/remove mutations) so the UI state
 * — "uploading", "fetching signed URL", etc. — stays scoped to the
 * one row the user is acting on. A 120-row table renders cheaply
 * either way and per-row state keeps the wiring straightforward.
 */
function ReceiptCell({
  slug,
  paymentNumber,
  receipt,
  readOnly,
  onUploaded,
  onCleared,
}: {
  slug: string;
  paymentNumber: number;
  receipt: LoanReceiptRef | null;
  readOnly: boolean;
  onUploaded: (ref: LoanReceiptRef) => void;
  onCleared: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadLoanReceipt();
  const remove = useRemoveLoanReceipt();
  const signedUrl = useLoanReceiptSignedUrl();

  const handleFile = async (file: File | null) => {
    if (!file) return;
    try {
      const ref = await upload.mutateAsync({
        loanSlug: slug,
        paymentNumber,
        file,
        previousPath: receipt?.path ?? null,
      });
      onUploaded(ref);
      toast.success(`Receipt uploaded for payment #${paymentNumber}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed.';
      toast.error(msg);
    } finally {
      // Reset the input so the same file can be re-selected later if
      // the operator needs to re-upload after a remove.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleView = async () => {
    if (!receipt) return;
    try {
      const url = await signedUrl.mutateAsync({ path: receipt.path });
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not open receipt.';
      toast.error(msg);
    }
  };

  const handleRemove = async () => {
    if (!receipt) return;
    try {
      await remove.mutateAsync({ path: receipt.path });
      onCleared();
      toast.success(`Receipt removed for payment #${paymentNumber}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not remove receipt.';
      toast.error(msg);
    }
  };

  // Read-only viewers (external lender) see only the View affordance —
  // no upload, no remove, no hidden file input. If there's no receipt
  // to view we render an em-dash so the column stays visually
  // aligned with the rest of the table.
  if (readOnly) {
    if (!receipt) {
      return <span className="text-xs text-muted-foreground">—</span>;
    }
    return (
      <div className="flex min-w-0 items-center gap-1.5">
        <FileText className="h-3.5 w-3.5 shrink-0 text-old-growth" />
        <button
          type="button"
          onClick={handleView}
          disabled={signedUrl.isPending}
          title={`${receipt.filename} · uploaded ${formatDate(receipt.uploadedAt)}`}
          className="inline-flex max-w-[10rem] items-center gap-1 truncate text-xs text-atmosphere-dark underline-offset-2 hover:underline disabled:opacity-50"
        >
          <span className="truncate">{receipt.filename}</span>
          {signedUrl.isPending ? (
            <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
          ) : (
            <ExternalLink className="h-3 w-3 shrink-0" />
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <input
        ref={inputRef}
        type="file"
        accept={RECEIPT_ACCEPT}
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
      />
      {receipt ? (
        <div className="flex min-w-0 items-center gap-1.5">
          <FileText className="h-3.5 w-3.5 shrink-0 text-old-growth" />
          <button
            type="button"
            onClick={handleView}
            disabled={signedUrl.isPending}
            title={`${receipt.filename} · uploaded ${formatDate(receipt.uploadedAt)}`}
            className="inline-flex max-w-[10rem] items-center gap-1 truncate text-xs text-atmosphere-dark underline-offset-2 hover:underline disabled:opacity-50"
          >
            <span className="truncate">{receipt.filename}</span>
            {signedUrl.isPending ? (
              <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
            ) : (
              <ExternalLink className="h-3 w-3 shrink-0" />
            )}
          </button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-7 shrink-0 p-0 text-muted-foreground hover:text-fresh-salmon-dark"
            onClick={handleRemove}
            disabled={remove.isPending}
            title="Remove receipt"
            aria-label={`Remove receipt for payment ${paymentNumber}`}
          >
            {remove.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <X className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 gap-1.5 px-2 text-xs"
          onClick={() => inputRef.current?.click()}
          disabled={upload.isPending}
        >
          {upload.isPending ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin" />
              Uploading…
            </>
          ) : (
            <>
              <Upload className="h-3 w-3" />
              Upload
            </>
          )}
        </Button>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="space-y-1">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="text-lg font-medium tabular-nums tracking-headline text-foreground">
        {value}
      </dd>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function termLabel(months: number): string {
  if (months % 12 === 0) {
    const years = months / 12;
    return `${years} yr${years === 1 ? '' : 's'}`;
  }
  return `${months} mo`;
}

interface LedgerTotals {
  paidCount: number;
  allPaid: boolean;
  /** Cents of principal not yet covered by a marked-paid payment. */
  principalRemainingCents: number;
  /** Sum of (principal + interest) across all remaining unpaid rows. */
  remainingOwedCents: number;
  /** Sum of interest across the entire life of the loan. */
  totalInterestCents: number;
}

/**
 * Roll up the table once per render. Cheap (O(n)) so no useMemo.
 * Kept colocated with the component since the math is wholly
 * coupled to the "paid means subtract from remaining" semantics.
 */
function computeTotals(
  schedule: ScheduledPayment[],
  records: PaymentRecords,
): LedgerTotals {
  let paidCount = 0;
  let principalRemainingCents = 0;
  let remainingOwedCents = 0;
  let totalInterestCents = 0;

  for (const p of schedule) {
    totalInterestCents += p.interestCents;
    if (records[p.number]?.paidOn) {
      paidCount += 1;
    } else {
      principalRemainingCents += p.principalCents;
      remainingOwedCents += p.paymentCents;
    }
  }

  return {
    paidCount,
    allPaid: schedule.length > 0 && paidCount === schedule.length,
    principalRemainingCents,
    remainingOwedCents,
    totalInterestCents,
  };
}
